import { ref, watch } from 'vue';

/**
 * Theme selection.
 *
 * Three states, not two: `system` is the default and follows the OS, so a user who has
 * chosen dark mode everywhere is not shown a white page on first load. An explicit
 * choice is remembered and wins over the OS from then on.
 *
 * The applied theme is written to `data-theme` on the root element, which is what the
 * token stylesheet keys on — so switching is a single attribute write with no rebuild
 * and no re-render.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * Set while the product ships a single theme. It wins over any stored preference and
 * over the OS, and the consoles hide their theme controls while it is set.
 *
 * The light theme's tokens are kept, so bringing it back means setting this to null
 * and removing the matching lock from the pre-paint script in each app's index.html.
 */
export const LOCKED_THEME: 'light' | 'dark' | null = 'dark';

const STORAGE_KEY = 'servepod.theme';

function readStored(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const value = localStorage.getItem(STORAGE_KEY);
  return value === 'light' || value === 'dark' ? value : 'system';
}

const preference = ref<ThemePreference>(readStored());
const resolved = ref<'light' | 'dark'>('dark');

function systemPrefersDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

function apply(): void {
  const theme =
    LOCKED_THEME ??
    (preference.value === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference.value);
  resolved.value = theme;
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

let watching = false;

export function useTheme() {
  if (!watching && typeof matchMedia !== 'undefined') {
    watching = true;
    // Follow the OS while the preference is `system`, but stop the moment the user
    // makes an explicit choice.
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (preference.value === 'system') apply();
    });
    watch(preference, (value) => {
      if (typeof localStorage !== 'undefined') {
        if (value === 'system') localStorage.removeItem(STORAGE_KEY);
        else localStorage.setItem(STORAGE_KEY, value);
      }
      apply();
    });
    apply();
  }

  return {
    preference,
    resolved,
    setTheme(value: ThemePreference) {
      preference.value = value;
    },
    toggle() {
      preference.value = resolved.value === 'dark' ? 'light' : 'dark';
    },
  };
}

/**
 * Applies the stored theme before Vue mounts.
 *
 * Called from a blocking inline script in index.html. Without it the page paints once
 * with the default theme and again with the chosen one, which is the flash of wrong
 * theme every dark-mode user notices immediately.
 */
export function applyStoredThemeEarly(): void {
  apply();
}
